// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, ImageBackground, Modal, Pressable, ScrollView } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { CreateAccountCta } from '@/components/CreateAccountCta';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getTourImageUri } from '@/services/tourImageSource';
import {
  createBffWebSocket,
  type HoleUpdateEvent,
  type TourLeaderboardErrorEvent,
  type TourLeaderboardStateEvent,
  type TourMemberClaimedEvent,
  type TourMemberClaimRejectedEvent,
  type TourMemberClaimReleasedEvent,
} from '@/services/bffWebSocket';
import { hasClaimAccess } from '@/services/claimHttp';
import {
  clearActiveClaimSession,
  ensureClaimClientId,
  getActiveClaimSessionSync,
  getClaimClientIdSync,
  loadClaimSession,
  setActiveClaimSession,
  subscribeClaimSession,
} from '@/services/claimSession';
import { createRequestId } from '@/services/requestId';
import { setCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import { styles } from '@/features/tour-leaderboard/styles';
import type { GameDefinition, Player, RoundSummary } from '@/features/tour-leaderboard/types';
import appColors from '@/theme/colors';

type InviteWsLifecycle = 'connecting' | 'connected-waiting-data' | 'received' | 'error';

type InviteMemberRow = {
  playerId: string;
  name: string;
  userId?: string | null;
  isTemporary: boolean;
};

const normalizeId = (value: unknown) => String(value ?? '').trim().toLowerCase();

/**
 * A claim only fails via tour.member.claim.rejected or this timeout — never via the send()
 * return value: 'queued' means the frame is flushed when the socket (re)opens and the claim is
 * still in flight.
 */
const CLAIM_REPLY_TIMEOUT_MS = 12000;
/**
 * Every round of the tour is claimable, a draft one included: claiming is how an invited guest gets
 * onto a round, and waiting for someone else to record the first score before they can is backwards.
 * The claim carries the picked round, so the claim token is scoped to it whether or not it started.
 */
const isRoundSelectableByStatus = (status: string | undefined): boolean => {
  const normalized = String(status ?? '').trim().toLowerCase();
  return (
    normalized === 'draft' ||
    normalized === 'open' ||
    normalized === 'active' ||
    normalized === 'playing' ||
    normalized === 'started' ||
    normalized === 'finished'
  );
};

export default function TourInviteScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { inviteToken: inviteTokenParam } = useLocalSearchParams<{ inviteToken?: string }>();
  const normalizedInviteToken = useMemo(() => String(inviteTokenParam ?? '').trim(), [inviteTokenParam]);

  const [tourId, setTourId] = useState<string | null>(null);
  const [tourName, setTourName] = useState<string | null>(null);
  const [tourImageKey, setTourImageKey] = useState<string | null>(null);
  const [roundCount, setRoundCount] = useState<number | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [rounds, setRounds] = useState<RoundSummary[]>([]);
  const [roundPickerOpen, setRoundPickerOpen] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);
  const [gameDefs, setGameDefs] = useState<GameDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wsLifecycle, setWsLifecycle] = useState<InviteWsLifecycle>('connecting');
  const [retryCount, setRetryCount] = useState(0);

  const [claimMessage, setClaimMessage] = useState<string | null>(null);
  const [claimRoutePending, setClaimRoutePending] = useState(false);
  const [claimWaitingForRoundSetup, setClaimWaitingForRoundSetup] = useState(false);
  const [claimedSourcePlayerId, setClaimedSourcePlayerId] = useState<string | null>(null);
  const [pendingClaimRequestByPlayerId, setPendingClaimRequestByPlayerId] = useState<Record<string, string>>({});
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const [claimClientId, setClaimClientId] = useState<string | null>(() => getClaimClientIdSync());
  const [liveClaimTokenOverride, setLiveClaimTokenOverride] = useState<string | null>(null);

  const isClaimGuestMode = !user?.token && hasClaimAccess(claimSession);
  const effectiveInviteToken = useMemo(
    () => normalizedInviteToken || claimSession?.inviteToken?.trim() || '',
    [claimSession?.inviteToken, normalizedInviteToken]
  );
  const allowAnonymousTourAccess = !user?.token && (!!effectiveInviteToken || isClaimGuestMode);
  const claimTokenForSocket = useMemo(
    () => liveClaimTokenOverride ?? (isClaimGuestMode ? (claimSession?.claimAccessToken ?? null) : null),
    [claimSession?.claimAccessToken, isClaimGuestMode, liveClaimTokenOverride]
  );
  const socketTourSubscriptionId = useMemo(
    () => (allowAnonymousTourAccess || !!claimTokenForSocket ? null : (tourId ?? null)),
    [allowAnonymousTourAccess, claimTokenForSocket, tourId]
  );

  const claimRoutePendingRef = useRef(false);
  const claimWaitingForRoundSetupRef = useRef(false);
  const claimedSourcePlayerIdRef = useRef<string | null>(null);
  const pendingClaimRoundByRequestIdRef = useRef<Record<string, string>>({});
  const gameDefsRef = useRef<GameDefinition[]>([]);
  const playersRef = useRef<Player[]>([]);
  const socketRef = useRef<ReturnType<typeof createBffWebSocket> | null>(null);
  if (!socketRef.current) {
    socketRef.current = createBffWebSocket(apiBaseUrl);
  }

  const setRoundAndHole = useRoundState((state) => state.setRoundAndHole);
  const setScorerOnlyMode = useRoundState((state) => state.setScorerOnlyMode);
  const setScorerPlayerId = useRoundState((state) => state.setScorerPlayerId);

  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'tour-invite',
      }, socketRef.current),
    [apiBaseUrl, user?.token]
  );

  const pageBackgroundImage = useMemo(
    () => getAssetImageSource('images/tour-leader-board.png', 'hero', '/tour/invite/[inviteToken]'),
    []
  );
  const trophyImage = useMemo(
    () => getAssetImageSource('images/trophy.png', 'icon', '/tour/invite/[inviteToken]'),
    []
  );

  // UUID-shaped even without crypto.randomUUID: the BFF deserializes requestId as a Guid, and the
  // old `tour-invite-<time>-<n>` fallback meant no native guest could ever claim a player.
  const buildRequestId = useCallback(() => createRequestId(), []);

  const logClaimConsole = useCallback((event: string, payload: Record<string, unknown>) => {
    if (typeof window === 'undefined') {
      return;
    }

    const output = {
      ...payload,
      inviteToken: effectiveInviteToken || null,
      tourId: tourId ?? null,
      claimClientId: claimClientId ?? null,
      userId: user?.id ?? null,
    };

    if (event === 'claim.rejected' || event === 'claim.send.failed' || event === 'claim.send.skipped') {
      console.warn(`[tour-invite] ${event}`, output);
      return;
    }

    console.info(`[tour-invite] ${event}`, output);
  }, [claimClientId, effectiveInviteToken, tourId, user?.id]);

  const tourImageUrl = useMemo(
    () => getTourImageUri(apiBaseUrl, tourId, tourImageKey),
    [apiBaseUrl, tourId, tourImageKey],
  );

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });

    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
      setClaimSessionLoaded(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    ensureClaimClientId()
      .then((resolvedClaimClientId) => {
        if (!cancelled) {
          setClaimClientId(resolvedClaimClientId);
        }
      })
      .catch(() => null);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    return () => {
      socket?.dispose();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    claimRoutePendingRef.current = claimRoutePending;
  }, [claimRoutePending]);

  useEffect(() => {
    claimWaitingForRoundSetupRef.current = claimWaitingForRoundSetup;
  }, [claimWaitingForRoundSetup]);

  useEffect(() => {
    claimedSourcePlayerIdRef.current = claimedSourcePlayerId;
  }, [claimedSourcePlayerId]);

  useEffect(() => {
    gameDefsRef.current = gameDefs;
  }, [gameDefs]);

  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  useEffect(() => {
    if (!claimSession?.claimAccessToken) {
      setLiveClaimTokenOverride((current) => (current ? null : current));
    }
  }, [claimSession?.claimAccessToken]);

  useEffect(() => {
    setPendingClaimRequestByPlayerId({});
    pendingClaimRoundByRequestIdRef.current = {};
    setClaimMessage(null);
    setClaimRoutePending(false);
    claimRoutePendingRef.current = false;
    setClaimedSourcePlayerId(null);
    claimedSourcePlayerIdRef.current = null;
    setClaimWaitingForRoundSetup(false);
    claimWaitingForRoundSetupRef.current = false;
  }, [tourId, user?.id]);

  useEffect(() => {
    if (!user?.token && !claimSessionLoaded) {
      return;
    }
    if (!claimClientId) {
      return;
    }
    if (!effectiveInviteToken && !tourId) {
      return;
    }

    const socket = socketRef.current;
    if (!socket) {
      return;
    }

    const requestedTourId = tourId ? normalizeId(tourId) : null;
    const startedAtMs = Date.now();
    const retryIntervalMs = 5000;
    const disconnectedErrorAfterMs = 20000;
    let snapshotReceived = false;
    let disconnectedSinceMs: number | null = null;
    let hasRequestedSnapshotSinceConnect = false;
    let retries = 0;
    let hasAdvancedAfterClaim = false;

    const resolveClaimedPlayerName = (sourcePlayerId?: string | null) => {
      const normalizedSourcePlayerId = normalizeId(sourcePlayerId);
      if (!normalizedSourcePlayerId) {
        return claimSession?.sourcePlayerName ?? null;
      }

      return (
        playersRef.current.find((player) => normalizeId(player.id) === normalizedSourcePlayerId)?.name ??
        claimSession?.sourcePlayerName ??
        null
      );
    };

    const resolvePlayableRoundId = (rawRounds: Array<Record<string, unknown>>) => {
      const normalizedRounds = rawRounds
        .map((round) => ({
          id: normalizeId((round.id as string | undefined) ?? (round.Id as string | undefined) ?? ''),
          status: String((round.status as string | undefined) ?? (round.Status as string | undefined) ?? '').toLowerCase(),
        }))
        .filter((round) => !!round.id);

      const activeRound = normalizedRounds.find((round) =>
        round.status === 'active' || round.status === 'playing' || round.status === 'started'
      );
      if (activeRound) {
        return activeRound.id;
      }

      const openRound = normalizedRounds.find((round) => round.status === 'open' || round.status === 'draft');
      return openRound?.id ?? null;
    };

    const routeToHoleOne = (
      resolvedRoundId: string,
      sourcePlayerId?: string | null,
      resolvedTourId?: string | null,
      claimAccessTokenOverride?: string | null
    ) => {
      if (hasAdvancedAfterClaim) {
        return;
      }

      hasAdvancedAfterClaim = true;
      setRoundAndHole(resolvedRoundId, 1);
      setScorerOnlyMode(true);
      setScorerPlayerId(sourcePlayerId ?? null);
      setClaimedSourcePlayerId(sourcePlayerId ?? null);
      claimedSourcePlayerIdRef.current = sourcePlayerId ?? null;
      setClaimWaitingForRoundSetup(false);
      claimWaitingForRoundSetupRef.current = false;
      setClaimRoutePending(false);
      claimRoutePendingRef.current = false;

      const latestClaimSession = getActiveClaimSessionSync();
      const resolvedInviteToken = effectiveInviteToken || latestClaimSession?.inviteToken || null;
      const resolvedClaimAccessToken = claimAccessTokenOverride ?? (latestClaimSession?.claimAccessToken ?? null);
      const nextClaimSession = {
        claimClientId,
        tourId: resolvedTourId ?? tourId ?? requestedTourId ?? '',
        sourcePlayerId: sourcePlayerId ?? null,
        sourcePlayerName: resolveClaimedPlayerName(sourcePlayerId),
        startedRoundId: resolvedRoundId,
        inviteToken: resolvedInviteToken,
        claimAccessToken: resolvedClaimAccessToken,
        claimedAt: new Date().toISOString(),
      };
      setClaimSession(nextClaimSession);
      void setActiveClaimSession(nextClaimSession);

      const params = new URLSearchParams();
      params.set('scorerOnly', 'true');
      if (sourcePlayerId) {
        params.set('playerId', sourcePlayerId);
      }

      const selectedGameIds = gameDefsRef.current
        .map((game) => normalizeId(game.id))
        .filter((id) => !!id);
      if (selectedGameIds.length > 0) {
        params.set('gameDefinitionIds', selectedGameIds.join(','));
      }

      router.push(`/round/${resolvedRoundId}/hole/1/compact?${params.toString()}`);
    };

    socket.batchUpdate(() => {
      socket.setInviteToken(effectiveInviteToken || null);
      socket.setClaimClientId(claimClientId);
      socket.setClaimAccessToken(claimTokenForSocket);
      socket.setSubscriptionContext({ tourId: socketTourSubscriptionId });
      if (user?.token) {
        socket.setAuthToken(user.token);
        socket.setAnonymousReadOnly(false);
        return;
      }

      socket.setAuthToken(null);
      socket.setAnonymousReadOnly(allowAnonymousTourAccess);
    });

    setLoading(true);
    setError(null);
    setRetryCount(0);
    setWsLifecycle('connecting');

    const normalizeTour = (rawTour: Record<string, unknown>) => {
      const nextTourName = (rawTour.tourName as string | undefined) ?? (rawTour.name as string | undefined) ?? null;
      const imageThumbKey =
        (rawTour.imageThumbKey as string | undefined) ??
        (rawTour.ImageThumbKey as string | undefined) ??
        null;
      const imageKey =
        (rawTour.imageKey as string | undefined) ??
        (rawTour.ImageKey as string | undefined) ??
        null;
      setTourName(nextTourName);
      setTourImageKey(imageThumbKey ?? imageKey);
    };

    const normalizeRounds = (rawRounds: Array<Record<string, unknown>>) => {
      const normalizedRounds = rawRounds.map((round) => ({
        id: normalizeId((round.id as string | undefined) ?? (round.Id as string | undefined) ?? ''),
        courseName: (round.courseName as string | undefined) ?? (round.CourseName as string | undefined),
        name: (round.name as string | undefined) ?? (round.Name as string | undefined),
        description: (round.description as string | undefined) ?? (round.Description as string | undefined),
        status: (round.status as string | undefined) ?? (round.Status as string | undefined),
        createdAt: (round.createdAt as string | undefined) ?? (round.CreatedAt as string | undefined),
      }));
      setRounds(normalizedRounds.filter((round) => !!round.id));
      setRoundCount(normalizedRounds.length);
    };

    const normalizePlayers = (rawPlayers: Array<Record<string, unknown>>) => {
      const normalizedPlayers = rawPlayers.map((player) => ({
        id: normalizeId((player.id as string | undefined) ?? (player.Id as string | undefined) ?? ''),
        name: (player.name as string | undefined) ?? (player.Name as string | undefined) ?? '',
        userId: (player.userId as string | undefined) ?? (player.UserId as string | undefined) ?? null,
        joinedAt: (player.joinedAt as string | undefined) ?? (player.JoinedAt as string | undefined),
        isTemporary: Boolean(
          (player.isTemporary as boolean | undefined) ??
          (player.IsTemporary as boolean | undefined) ??
          false
        ),
      }));
      setPlayers(normalizedPlayers.filter((player) => !!player.id));
    };

    const normalizeGames = (rawGames: Array<Record<string, unknown>>) => {
      const normalizedGames = rawGames.map((game) => ({
        id: normalizeId((game.id as string | undefined) ?? (game.Id as string | undefined) ?? ''),
        name: (game.name as string | undefined) ?? (game.Name as string | undefined),
      }));
      setGameDefs(normalizedGames.filter((game) => !!game.id));
    };

    const applySnapshot = (message: TourLeaderboardStateEvent) => {
      const incomingTourId = normalizeId(message.tourId);
      if (!incomingTourId) {
        return;
      }
      if (requestedTourId && incomingTourId !== requestedTourId) {
        logToBff('tourInvite.ws.dropTourMismatch', {
          expectedTourId: requestedTourId,
          messageTourId: message.tourId,
          type: message.type,
        }, 'debug');
        return;
      }

      setTourId((current) => current ?? message.tourId ?? null);
      normalizeTour((message.tour ?? {}) as Record<string, unknown>);
      const rawRounds = (message.rounds ?? []) as Array<Record<string, unknown>>;
      normalizeRounds(rawRounds);
      normalizePlayers((message.players ?? []) as Array<Record<string, unknown>>);
      normalizeGames((message.games ?? []) as Array<Record<string, unknown>>);

      if (claimRoutePendingRef.current && claimWaitingForRoundSetupRef.current) {
        const fallbackRoundId = resolvePlayableRoundId(rawRounds);
        if (fallbackRoundId) {
          routeToHoleOne(fallbackRoundId, claimedSourcePlayerIdRef.current, message.tourId ?? null);
        }
      }

      if (!snapshotReceived) {
        logToBff('tourInvite.ws.firstSnapshot', {
          tourId: incomingTourId,
          latencyMs: Date.now() - startedAtMs,
        }, 'debug');
      }

      snapshotReceived = true;
      setError(null);
      setLoading(false);
      setWsLifecycle('received');
    };

    const requestSnapshot = (reason: string) => {
      hasRequestedSnapshotSinceConnect = true;
      const requestId = buildRequestId();
      socket.sendTourLeaderboardRequest({
        tourId: tourId ?? undefined,
        inviteToken: effectiveInviteToken || undefined,
        requestId,
      });
      logToBff('tourInvite.ws.request', {
        tourId,
        inviteToken: effectiveInviteToken || null,
        requestId,
        reason,
        retryCount: retries,
      }, 'debug');
      if (typeof window !== 'undefined') {
        console.info('[tour-invite] ws request', {
          tourId,
          inviteToken: effectiveInviteToken || null,
          requestId,
          reason,
          retryCount: retries,
        });
      }
    };

    const unsubscribeMessage = socket.onMessage((message) => {
      if (message.type === 'tour.leaderboard.state') {
        applySnapshot(message as TourLeaderboardStateEvent);
        return;
      }

      if (message.type === 'tour.leaderboard.error') {
        const err = message as TourLeaderboardErrorEvent;
        if (requestedTourId && err.tourId && normalizeId(err.tourId) !== requestedTourId) {
          return;
        }

        const code = String(err.code ?? '').trim();
        const isTerminal = code === 'tour.leaderboard.invalidTourId' || code === 'tour.leaderboard.invalidInviteToken';
        if (isTerminal) {
          setError(t('tourLeaderboard.loadError'));
          setLoading(false);
          setWsLifecycle('error');
        } else {
          setError(null);
          setLoading(true);
          setWsLifecycle('connected-waiting-data');
        }
        logToBff('tourInvite.ws.error', {
          tourId: requestedTourId ?? err.tourId,
          code: err.code,
          message: err.message,
          terminal: isTerminal,
        }, 'warn');
        return;
      }

      if (message.type === 'tour.member.claimed') {
        const claim = message as TourMemberClaimedEvent;
        if (claim.tourId && requestedTourId && normalizeId(claim.tourId) !== requestedTourId) {
          return;
        }

        const ownsClaimEvent = !!claim.claimClientId && normalizeId(claim.claimClientId) === normalizeId(claimClientId);
        if (!ownsClaimEvent) {
          requestSnapshot('claim-observed');
          return;
        }

        if (claim.sourcePlayerId) {
          setClaimedSourcePlayerId(claim.sourcePlayerId);
          claimedSourcePlayerIdRef.current = claim.sourcePlayerId;
        }

        const requestedRoundId = claim.requestId
          ? pendingClaimRoundByRequestIdRef.current[claim.requestId] ?? null
          : null;
        const resolvedClaimRoundId = requestedRoundId ?? claim.startedRoundId ?? null;
        const hasTokenRoundScopeMismatch =
          !!claim.claimToken &&
          !!requestedRoundId &&
          !!claim.startedRoundId &&
          normalizeId(requestedRoundId) !== normalizeId(claim.startedRoundId);
        const effectiveClaimAccessToken = hasTokenRoundScopeMismatch ? null : (claim.claimToken ?? null);

        if (!claim.claimToken) {
          logClaimConsole('claim.missingToken', {
            requestId: claim.requestId ?? null,
            sourcePlayerId: claim.sourcePlayerId ?? null,
            startedRoundId: claim.startedRoundId ?? null,
            claimedAt: claim.claimedAt ?? null,
          });
          setPendingClaimRequestByPlayerId((pending) => {
            if (!claim.sourcePlayerId || !pending[claim.sourcePlayerId]) {
              return pending;
            }

            const next = { ...pending };
            delete next[claim.sourcePlayerId];
            return next;
          });
          if (claim.requestId) {
            delete pendingClaimRoundByRequestIdRef.current[claim.requestId];
          }
          setClaimRoutePending(false);
          claimRoutePendingRef.current = false;
          setClaimWaitingForRoundSetup(false);
          claimWaitingForRoundSetupRef.current = false;
          setClaimMessage(t('tourLeaderboard.claimTokenMissing'));
          setLiveClaimTokenOverride(null);
          setClaimSession(null);
          void clearActiveClaimSession();
          return;
        }

        setPendingClaimRequestByPlayerId((pending) => {
          if (!claim.sourcePlayerId || !pending[claim.sourcePlayerId]) {
            return pending;
          }

          const next = { ...pending };
          delete next[claim.sourcePlayerId];
          return next;
        });
        if (claim.requestId) {
          delete pendingClaimRoundByRequestIdRef.current[claim.requestId];
        }

        const nextClaimSession = {
          claimClientId,
          tourId: claim.tourId ?? tourId ?? requestedTourId ?? '',
          sourcePlayerId: claim.sourcePlayerId ?? null,
          sourcePlayerName: resolveClaimedPlayerName(claim.sourcePlayerId),
          startedRoundId: resolvedClaimRoundId,
          inviteToken: effectiveInviteToken || null,
          claimAccessToken: effectiveClaimAccessToken,
          claimedAt: claim.claimedAt ?? new Date().toISOString(),
        };

        setLiveClaimTokenOverride(effectiveClaimAccessToken);
        socket.batchUpdate(() => {
          socket.setInviteToken(effectiveInviteToken || null);
          socket.setClaimClientId(claim.claimClientId ?? claimClientId ?? null);
          socket.setClaimAccessToken(effectiveClaimAccessToken);
          socket.setSubscriptionContext({ tourId: null });
          socket.setAuthToken(null);
          socket.setAnonymousReadOnly(true);
        });

        setClaimSession(nextClaimSession);
        void setActiveClaimSession(nextClaimSession);

        logClaimConsole('claim.accepted', {
          requestId: claim.requestId ?? null,
          sourcePlayerId: claim.sourcePlayerId ?? null,
          requestedRoundId,
          startedRoundId: claim.startedRoundId ?? null,
          resolvedRoundId: resolvedClaimRoundId,
          hasClaimToken: !!effectiveClaimAccessToken,
          claimTokenRoundScopeMismatch: hasTokenRoundScopeMismatch,
          claimedAt: claim.claimedAt ?? null,
        });

        if (resolvedClaimRoundId) {
          routeToHoleOne(
            resolvedClaimRoundId,
            claim.sourcePlayerId ?? null,
            claim.tourId ?? null,
            effectiveClaimAccessToken
          );
        } else {
          setClaimMessage(t('tourLeaderboard.claimSuccess'));
          setClaimWaitingForRoundSetup(true);
          claimWaitingForRoundSetupRef.current = true;
          setClaimRoutePending(true);
          claimRoutePendingRef.current = true;
        }

        requestSnapshot('claim-success');
        return;
      }

      if (message.type === 'tour.member.claim.rejected') {
        const claimRejected = message as TourMemberClaimRejectedEvent;
        if (claimRejected.tourId && requestedTourId && normalizeId(claimRejected.tourId) !== requestedTourId) {
          return;
        }
        if (claimRejected.claimClientId && normalizeId(claimRejected.claimClientId) !== normalizeId(claimClientId)) {
          return;
        }

        setPendingClaimRequestByPlayerId((pending) => {
          if (!claimRejected.sourcePlayerId || !pending[claimRejected.sourcePlayerId]) {
            return pending;
          }

          const next = { ...pending };
          delete next[claimRejected.sourcePlayerId];
          return next;
        });
        if (claimRejected.requestId) {
          delete pendingClaimRoundByRequestIdRef.current[claimRejected.requestId];
        }

        const translated = claimRejected.messageKey ? t(claimRejected.messageKey) : null;
        logClaimConsole('claim.rejected', {
          requestId: claimRejected.requestId ?? null,
          sourcePlayerId: claimRejected.sourcePlayerId ?? null,
          code: claimRejected.code ?? null,
          messageKey: claimRejected.messageKey ?? null,
          message: claimRejected.message ?? null,
          rejectedAt: claimRejected.rejectedAt ?? null,
        });
        setClaimMessage(
          translated && claimRejected.messageKey && translated !== claimRejected.messageKey
            ? translated
            : claimRejected.message ?? t('tourLeaderboard.claimRejected')
        );
        setClaimRoutePending(false);
        claimRoutePendingRef.current = false;
        setClaimWaitingForRoundSetup(false);
        claimWaitingForRoundSetupRef.current = false;
        requestSnapshot('claim-rejected');
        return;
      }

      if (message.type === 'tour.member.claim.released') {
        const claimReleased = message as TourMemberClaimReleasedEvent;
        if (claimReleased.tourId && requestedTourId && normalizeId(claimReleased.tourId) !== requestedTourId) {
          return;
        }

        const ownsReleaseEvent =
          !!claimReleased.claimClientId &&
          normalizeId(claimReleased.claimClientId) === normalizeId(claimClientId);
        if (!ownsReleaseEvent) {
          requestSnapshot('claim-release-observed');
          return;
        }

        setPendingClaimRequestByPlayerId({});
        setClaimRoutePending(false);
        claimRoutePendingRef.current = false;
        setClaimWaitingForRoundSetup(false);
        claimWaitingForRoundSetupRef.current = false;
        setLiveClaimTokenOverride(null);
        setClaimedSourcePlayerId(null);
        claimedSourcePlayerIdRef.current = null;
        setClaimMessage(t('tourLeaderboard.claimReleased'));
        setClaimSession(null);
        void clearActiveClaimSession();
        return;
      }

      if (message.type === 'hole.update') {
        const holeUpdate = message as HoleUpdateEvent;
        if (!claimRoutePendingRef.current || !claimWaitingForRoundSetupRef.current) {
          return;
        }
        if (requestedTourId && holeUpdate.tourId && normalizeId(holeUpdate.tourId) !== requestedTourId) {
          return;
        }
        if (holeUpdate.hole !== 1 || !holeUpdate.entries?.length || !holeUpdate.roundId) {
          return;
        }

        routeToHoleOne(holeUpdate.roundId, claimedSourcePlayerIdRef.current, holeUpdate.tourId ?? null);
        return;
      }

      if (message.type === 'error') {
        const wsError = message as { code?: string; message?: string };
        if (wsError.code !== 'ws.anonymous.readOnly') {
          return;
        }

        setPendingClaimRequestByPlayerId({});
        setClaimMessage(wsError.message ?? t('tourLeaderboard.claimRejected'));
      }
    });

    requestSnapshot('initial');
    setWsLifecycle('connected-waiting-data');

    const retryInterval = setInterval(() => {
      if (snapshotReceived) {
        return;
      }

      retries += 1;
      setRetryCount(retries);
      setWsLifecycle('connected-waiting-data');
      requestSnapshot('interval-retry');
      logToBff('tourInvite.ws.retry', {
        tourId,
        inviteToken: effectiveInviteToken || null,
        retryCount: retries,
      }, 'debug');

      if (disconnectedSinceMs && Date.now() - disconnectedSinceMs >= disconnectedErrorAfterMs) {
        setError(t('tourLeaderboard.loadError'));
        setLoading(false);
        setWsLifecycle('error');
        logToBff('tourInvite.ws.prolongedDisconnect', {
          tourId,
          inviteToken: effectiveInviteToken || null,
          disconnectedMs: Date.now() - disconnectedSinceMs,
        }, 'warn');
      }
    }, retryIntervalMs);

    const unsubscribeConnection = socket.onConnectionChange((state) => {
      logToBff('tourInvite.ws.connection', {
        tourId,
        inviteToken: effectiveInviteToken || null,
        state,
      }, 'debug');
      if (state === 'connected') {
        disconnectedSinceMs = null;
        if (!snapshotReceived) {
          setWsLifecycle('connected-waiting-data');
        }
        if (!hasRequestedSnapshotSinceConnect) {
          requestSnapshot('connected');
        }
      } else if (!snapshotReceived) {
        hasRequestedSnapshotSinceConnect = false;
        if (!disconnectedSinceMs) {
          disconnectedSinceMs = Date.now();
        }
        setWsLifecycle('connecting');
      }
    });

    return () => {
      clearInterval(retryInterval);
      unsubscribeMessage();
      unsubscribeConnection();
      socket.batchUpdate(() => {
        socket.setSubscriptionContext({ roundId: null, tourId: null });
        socket.setInviteToken(null);
        socket.setClaimAccessToken(null);
        socket.setClaimClientId(null);
        if (!user?.token) {
          socket.setAnonymousReadOnly(false);
          socket.setAuthToken(null);
          return;
        }

        socket.setAnonymousReadOnly(false);
      });
    };
  }, [
    allowAnonymousTourAccess,
    buildRequestId,
    claimClientId,
    claimSession,
    claimSessionLoaded,
    claimTokenForSocket,
    effectiveInviteToken,
    liveClaimTokenOverride,
    logToBff,
    router,
    setRoundAndHole,
    setScorerOnlyMode,
    setScorerPlayerId,
    socketTourSubscriptionId,
    t,
    tourId,
    user?.token,
  ]);

  const handleClaimPlayer = useCallback((sourcePlayerId: string) => {
    if (!claimClientId || !selectedRoundId) {
      logClaimConsole('claim.send.skipped', {
        sourcePlayerId,
        reason: !claimClientId ? 'missing-claim-client-id' : 'missing-selected-round',
      });
      setClaimMessage(t('tourLeaderboard.claimSendError'));
      return;
    }

    const requestId = buildRequestId();
    logClaimConsole('claim.send', {
      sourcePlayerId,
      requestId,
      authenticated: !!user?.token,
    });
    setPendingClaimRequestByPlayerId((pending) => ({
      ...pending,
      [sourcePlayerId]: requestId,
    }));
    pendingClaimRoundByRequestIdRef.current[requestId] = selectedRoundId;
    setClaimMessage(t('tourLeaderboard.claimPending'));

    // Ignore the send() outcome: 'queued' means the claim is still in flight (flushed on
    // reconnect). The pending-claim timeout effect below is the only thing that gives up.
    socketRef.current?.sendTourMemberClaim(
      tourId ?? undefined,
      sourcePlayerId,
      requestId,
      effectiveInviteToken || undefined,
      selectedRoundId
    );
  }, [buildRequestId, claimClientId, effectiveInviteToken, logClaimConsole, selectedRoundId, t, tourId, user?.token]);

  // "Just watch": no claim, no player, nothing persisted — the invite token in the URL is the whole
  // credential, so it has to ride along in the route. push (not replace) keeps the claim options
  // one Back away.
  const viewerTourId = tourId ?? claimSession?.tourId ?? null;
  const handleViewAsSpectator = useCallback(() => {
    if (!viewerTourId) {
      return;
    }

    logToBff('tourInvite.viewer.open', {
      tourId: viewerTourId,
      inviteToken: effectiveInviteToken || null,
    }, 'debug');
    router.push({
      pathname: '/tour/[tourId]/leaderboard',
      params: {
        tourId: viewerTourId,
        inviteToken: effectiveInviteToken,
        viewer: '1',
      },
    });
  }, [effectiveInviteToken, logToBff, router, viewerTourId]);

  // Gives up on claims that never get a reply (claimed or rejected both clear the pending map).
  useEffect(() => {
    const pendingEntries = Object.entries(pendingClaimRequestByPlayerId);
    if (pendingEntries.length === 0) {
      return;
    }
    const timeout = setTimeout(() => {
      logClaimConsole('claim.timeout', {
        pending: pendingEntries.map(([playerId, requestId]) => ({ playerId, requestId })),
      });
      pendingEntries.forEach(([, requestId]) => {
        delete pendingClaimRoundByRequestIdRef.current[requestId];
      });
      setPendingClaimRequestByPlayerId({});
      setClaimMessage(t('tourLeaderboard.claimTimeout'));
    }, CLAIM_REPLY_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [logClaimConsole, pendingClaimRequestByPlayerId, t]);

  const handleReleaseClaim = useCallback(() => {
    if (!claimWaitingForRoundSetup || !claimClientId) {
      return;
    }

    const requestId = buildRequestId();
    // 'queued' is not a failure — the release is flushed when the socket (re)opens and the
    // tour.member.claim.released reply drives the UI.
    socketRef.current?.sendTourMemberClaimRelease(
      tourId ?? undefined,
      requestId,
      effectiveInviteToken || undefined
    );
  }, [buildRequestId, claimClientId, claimWaitingForRoundSetup, effectiveInviteToken, tourId]);

  const showClaimSessionLoading = !user?.token && !claimSessionLoaded;
  const activeClaimedPlayerId = claimedSourcePlayerId ?? claimSession?.sourcePlayerId ?? null;
  const currentUserPlayerId =
    players.find((player) => !!user?.id && player.userId?.toLowerCase() === user.id.toLowerCase())?.id ?? null;
  const membersRowsForView = useMemo<InviteMemberRow[]>(
    () =>
      players
        .map((player) => ({
          playerId: player.id,
          name: player.name || t('tourLeaderboard.unknownPlayer'),
          userId: player.userId,
          isTemporary: !!player.isTemporary || !player.userId,
        }))
        .sort((a, b) => {
          const aIsClaimed = !!activeClaimedPlayerId && a.playerId === activeClaimedPlayerId;
          const bIsClaimed = !!activeClaimedPlayerId && b.playerId === activeClaimedPlayerId;

          if (aIsClaimed && !bIsClaimed) {
            return -1;
          }
          if (!aIsClaimed && bIsClaimed) {
            return 1;
          }

          return a.name.localeCompare(b.name);
        }),
    [activeClaimedPlayerId, players, t]
  );

  const hasRenderableData = membersRowsForView.length > 0 || rounds.length > 0 || !!tourName;
  const courseSummary = useMemo(() => {
    const uniqueCourseNames = Array.from(
      new Set(
        rounds
          .map((round) => round.courseName?.trim() || round.name?.trim() || '')
          .filter((value) => value.length > 0)
      )
    );

    if (uniqueCourseNames.length === 0) {
      return null;
    }

    return uniqueCourseNames.join(', ');
  }, [rounds]);
  const wsStatusMessage = useMemo(() => {
    if (!loading || error) {
      return null;
    }

    if (wsLifecycle === 'connecting') {
      return t('tourLeaderboard.wsConnecting');
    }

    if (wsLifecycle === 'connected-waiting-data') {
      if (retryCount > 0) {
        return t('tourLeaderboard.wsRetrying', { count: retryCount });
      }

      return t('tourLeaderboard.wsWaitingData');
    }

    return null;
  }, [error, loading, retryCount, t, wsLifecycle]);

  const selectableRounds = useMemo(
    () => rounds.filter((round) => isRoundSelectableByStatus(round.status)),
    [rounds]
  );

  const selectedRound = useMemo(
    () => selectableRounds.find((round) => round.id === selectedRoundId) ?? null,
    [selectableRounds, selectedRoundId]
  );

  const selectDefaultRoundId = useCallback((candidates: RoundSummary[]): string | null => {
    const normalized = candidates
      .map((round) => ({
        id: normalizeId(round.id),
        status: String(round.status ?? '').toLowerCase(),
      }))
      .filter((round) => !!round.id);
    if (normalized.length === 0) {
      return null;
    }

    const preferred = normalized.find((round) =>
      round.status === 'active' || round.status === 'playing' || round.status === 'started'
    ) ?? normalized.find((round) => round.status === 'open' || round.status === 'draft');

    return preferred?.id ?? normalized[0].id;
  }, []);

  useEffect(() => {
    if (selectableRounds.length === 0) {
      setSelectedRoundId(null);
      return;
    }

    if (selectedRoundId && selectableRounds.some((round) => round.id === selectedRoundId)) {
      return;
    }

    setSelectedRoundId(selectDefaultRoundId(selectableRounds));
  }, [selectableRounds, selectDefaultRoundId, selectedRoundId]);

  const canClaimPlayer = (member: InviteMemberRow) =>
    (!!tourId || !!effectiveInviteToken) &&
    !claimWaitingForRoundSetup &&
    !!selectedRoundId &&
    member.isTemporary &&
    member.playerId !== currentUserPlayerId;

  // Watching needs no round selection (unlike claiming) — only a tour to point the leaderboard
  // route at. The tour id arrives with the leaderboard snapshot, or from a resumed claim session.
  const canViewAsSpectator =
    !!viewerTourId && !!effectiveInviteToken && !claimWaitingForRoundSetup && !claimRoutePending;

  if (!effectiveInviteToken) {
    return (
      <View testID="invite-missing-token" style={[styles.background, styles.container]}>
        <Text style={styles.title}>{t('tourLeaderboard.title')}</Text>
        <Text style={styles.meta}>{t('tourLeaderboard.missingTour')}</Text>
        <GoffyButton
          label={t('tourLeaderboard.goTours')}
          onPress={() => router.push('/my-tours')}
          tone="accent"
          oldButtonStyle={styles.primaryButton}
          oldTextStyle={styles.primaryText}
          style={{ width: 220, height: 60 }}
          textStyle={{ fontSize: 16 }}
        />
      </View>
    );
  }

  return (
    <ImageBackground
      source={pageBackgroundImage}
      style={styles.background}
      imageStyle={styles.backgroundImage}
      resizeMode="cover"
    >
      <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
        <View style={styles.headerCard}>
          {tourImageUrl ? (
            <Image source={{ uri: tourImageUrl }} style={styles.headerImage} resizeMode="cover" />
          ) : (
            <Image source={trophyImage} style={styles.headerImage} resizeMode="contain" />
          )}
          <View style={styles.headerText}>
            <Text style={styles.bannerTitle}>{tourName ?? t('tourInvite.title')}</Text>
            {roundCount !== null ? (
              <Text style={styles.bannerMeta}>{t('tourLeaderboard.roundsTitle')}: {roundCount}</Text>
            ) : null}
            {courseSummary ? (
              <Text style={styles.bannerMeta}>{t('tourInvite.courseLabel')}: {courseSummary}</Text>
            ) : null}
          </View>
        </View>

        {showClaimSessionLoading ? (
          <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
            <ActivityIndicator color={appColors.text.primary} />
          </View>
        ) : (
          <>
            {loading && !hasRenderableData ? <ActivityIndicator color={appColors.text.primary} /> : null}
            {loading && !hasRenderableData && wsStatusMessage ? <Text style={styles.meta}>{wsStatusMessage}</Text> : null}
            {error ? <Text testID="invite-error" style={styles.error}>{error}</Text> : null}

            {claimWaitingForRoundSetup ? (
              <View style={styles.waitingCard}>
                <Text style={styles.waitingTitle}>{t('tourLeaderboard.waitingForRoundSetupTitle')}</Text>
                <Text style={styles.waitingText}>{t('tourLeaderboard.waitingForRoundSetup')}</Text>
                <ActivityIndicator color={appColors.text.primary} />
                <GoffyButton
                  label={t('tourLeaderboard.claimReleaseCta')}
                  onPress={handleReleaseClaim}
                  size="compact"
                  tone="dark"
                  oldButtonStyle={styles.resultsActionButton}
                  oldTextStyle={styles.resultsActionButtonText}
                  style={styles.resultsActionButton}
                  textStyle={styles.resultsActionButtonText}
                />
              </View>
            ) : null}

            {!claimWaitingForRoundSetup && claimMessage ? <Text style={styles.meta}>{claimMessage}</Text> : null}

            {canViewAsSpectator ? (
              <View style={styles.membersCard} testID="invite-viewer-card">
                <Text style={styles.membersTitle}>{t('tourInvite.viewerTitle')}</Text>
                <Text style={styles.membersHint}>{t('tourInvite.viewerHint')}</Text>
                <GoffyButton
                  testID="invite-viewer-button"
                  label={t('tourInvite.viewerCta')}
                  onPress={handleViewAsSpectator}
                  tone="dark"
                  size="compact"
                  accessibilityLabel={t('tourInvite.viewerCta')}
                />
              </View>
            ) : null}

            {rounds.length ? (
              <View style={styles.roundPickerCard}>
                <Text style={styles.roundPickerTitle}>{t('scorecard.selectRoundTitle')}</Text>
                <Pressable testID="invite-round-picker" style={styles.roundPicker} onPress={() => setRoundPickerOpen(true)}>
                  <Text style={styles.roundPickerText} numberOfLines={1}>
                    {selectedRound?.name ?? selectedRound?.courseName ?? t('scorecard.selectRoundFallback')}
                  </Text>
                  <Text style={styles.roundPickerHint}>{t('scorecard.selectRound')}</Text>
                </Pressable>
              </View>
            ) : null}

            {/* requireClaimedPlayer: this screen exists to claim a player, and the offer would
                otherwise appear beside the claim list for anyone who merely opened the link. */}
            <CreateAccountCta
              testID="invite-claim-signup-card"
              ctaTestID="invite-claim-signup-cta"
              requireClaimedPlayer
            />

            <View style={styles.membersCard}>
              <Text style={styles.membersTitle}>{t('tourLeaderboard.membersTitle')}</Text>
              <Text style={styles.membersHint}>{t('tourLeaderboard.membersClaimHint')}</Text>
              <View testID="invite-members-list" style={styles.membersList}>
                {membersRowsForView.length ? (
                  membersRowsForView.map((member, index) => {
                    const isPending = !!pendingClaimRequestByPlayerId[member.playerId];
                    const isClaimed = !!activeClaimedPlayerId && member.playerId === activeClaimedPlayerId;
                    const memberStatus = isClaimed || !member.isTemporary
                      ? t('tourLeaderboard.memberClaimed')
                      : t('tourLeaderboard.memberTemporary');

                    return (
                      <View
                        key={member.playerId}
                        testID={`invite-member-row-${member.playerId}`}
                        style={[
                          styles.memberRow,
                          index % 2 === 0 ? styles.memberRowEven : styles.memberRowOdd,
                        ]}
                      >
                        <View style={styles.memberMain}>
                          <Text style={styles.memberName}>{member.name}</Text>
                          <Text style={styles.memberMeta}>{memberStatus}</Text>
                        </View>
                        {canClaimPlayer(member) ? (
                          <View style={styles.claimCell}>
                            <Pressable
                              testID={`invite-claim-button-${member.playerId}`}
                              style={[
                                styles.claimButton,
                                isPending ? styles.claimButtonDisabled : null,
                              ]}
                              onPress={() => handleClaimPlayer(member.playerId)}
                              disabled={isPending}
                            >
                              <Text style={styles.claimButtonText}>
                                {isPending ? t('tourLeaderboard.claiming') : t('tourLeaderboard.claimCta')}
                              </Text>
                            </Pressable>
                          </View>
                        ) : null}
                      </View>
                    );
                  })
                ) : (
                  <Text style={styles.membersEmpty}>
                    {loading ? t('tourLeaderboard.wsWaitingData') : t('tourLeaderboard.membersEmpty')}
                  </Text>
                )}
              </View>
            </View>
          </>
        )}
      </ScrollView>
      <Modal visible={roundPickerOpen} transparent animationType="fade" onRequestClose={() => setRoundPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setRoundPickerOpen(false)}>
          <Pressable style={[styles.modalCard, styles.roundPickerModalCard]} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('scorecard.selectRoundTitle')}</Text>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.roundPickerOptions}>
                {rounds.map((round) => {
                  const isSelectable = isRoundSelectableByStatus(round.status);
                  const status = String(round.status ?? '-').trim() || '-';
                  return (
                    <Pressable
                      key={round.id}
                      testID={`invite-round-option-${round.id}`}
                      style={[styles.roundPickerOption, !isSelectable ? { opacity: 0.5 } : null]}
                      onPress={() => {
                        if (!isSelectable) {
                          return;
                        }

                        setSelectedRoundId(round.id);
                        setRoundPickerOpen(false);
                      }}
                      disabled={!isSelectable}
                    >
                      <Text style={styles.roundPickerOptionTitle}>
                        {round.name ?? round.courseName ?? t('scorecard.selectRoundFallback')}
                      </Text>
                      <Text style={styles.roundPickerOptionSubtitle}>
                        {isSelectable ? status : `${status} (Not started)`}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </ScrollView>
            <View style={styles.modalButtonRow}>
              <GoffyButton
                label={t('tourLeaderboard.resultsClose')}
                onPress={() => setRoundPickerOpen(false)}
                size="compact"
                tone="dark"
                oldButtonStyle={styles.modalCloseButton}
                oldTextStyle={styles.modalCloseText}
                style={styles.modalActionButton}
                textStyle={styles.modalActionText}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </ImageBackground>
  );
}
